import { useEffect, useRef, useState } from 'react'

const KIND_LABELS = {
  password: 'Temporary password',
  api_key:  'New API Key created',
}

// Hardening: the raw secret value is held in a `useRef` instead of `useState`
// so it never surfaces in the React DevTools Components panel as a hooked
// state value. The secret still renders to the DOM once (necessary — the
// agent must copy it), but it does NOT appear in the React component tree as
// inspectable state. On unmount, the ref is nulled so any lingering reference
// drops out of memory at the next GC. Dismissal toggles a boolean flag, not
// the secret itself.
export default function SecretReveal({ reveal }) {
  const rawValueRef = useRef(reveal?.value ?? null)
  const [visible, setVisible] = useState(Boolean(reveal?.value))

  // Keep the ref in sync if the parent re-renders with a fresh reveal payload
  // (e.g. a second key created in the same session). We deliberately do NOT
  // expose `reveal.value` via state to avoid surfacing it in DevTools.
  useEffect(() => {
    rawValueRef.current = reveal?.value ?? null
    setVisible(Boolean(reveal?.value))
  }, [reveal?.value])

  // Cleanup on unmount: drop the strong reference so the secret string
  // becomes eligible for garbage collection sooner.
  useEffect(() => {
    return () => { rawValueRef.current = null }
  }, [])

  if (!reveal || !visible || !rawValueRef.current) return null

  const title = KIND_LABELS[reveal.kind] ?? 'New secret created'

  function handleCopy() {
    if (rawValueRef.current) {
      navigator.clipboard.writeText(rawValueRef.current)
    }
  }

  function handleDismiss() {
    rawValueRef.current = null
    setVisible(false)
  }

  return (
    <div className="border border-amber-700 bg-amber-950/60 rounded p-4 font-mono text-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 space-y-2">
          <p className="text-amber-400 font-semibold">{title}</p>
          <p className="text-zinc-400 text-xs">{reveal.label}</p>
          <div className="flex items-center gap-2 flex-wrap">
            <code className="px-2 py-1 rounded bg-zinc-900 text-emerald-400 text-xs break-all select-all">
              {rawValueRef.current}
            </code>
            <button
              onClick={handleCopy}
              className="px-2 py-1 rounded text-xs text-zinc-300 bg-zinc-800 hover:bg-zinc-700 hover:text-zinc-100 transition-colors"
            >
              Copy
            </button>
          </div>
          <p className="text-amber-600 text-xs">
            This value is shown only once. Copy it now.
          </p>
        </div>
        <button
          onClick={handleDismiss}
          className="text-zinc-500 hover:text-zinc-200 transition-colors text-lg leading-none shrink-0"
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>
    </div>
  )
}
